import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Bed, Moon, Star } from "lucide-react";

const SleepWidget: React.FC = () => {
    const { t, i18n } = useTranslation("healthSensors");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const widget = t("tabs.0.widget", { returnObjects: true }) as any;

    return (
        <motion.div className="relative w-full" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ duration: 0.5 }}>
            {/* Main card */}
            <div className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-indigo-100">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                        <motion.div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center ml-2" whileHover={{ scale: 1.1, rotate: 5 }} whileTap={{ scale: 0.95 }}>
                            <Moon className="w-5 h-5 text-indigo-600" />
                        </motion.div>
                        <h5 className="text-indigo-800 font-medium">{widget.reportTitle}</h5>
                    </div>
                    <motion.span
                        className="text-xs bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-medium"
                        whileHover={{ scale: 1.05, backgroundColor: "#e0e7ff" }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {widget.duration}
                    </motion.span>
                </div>

                {/* Sleep phases chart */}
                <div className="relative h-24 mb-3 overflow-hidden rounded-lg">
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-blue-500 opacity-10"></div>
                    <div className="relative z-10 h-full flex">
                        <motion.div
                            className="h-full w-1/4 bg-indigo-200 flex items-end justify-center text-xs text-indigo-700 font-medium"
                            initial={{ height: 0 }}
                            animate={{ height: "100%" }}
                            transition={{ duration: 1, delay: 0.2 }}
                        >
                            <span className="mb-1">{widget.phases.light}</span>
                        </motion.div>
                        <motion.div
                            className="h-full w-2/5 bg-indigo-400 flex items-end justify-center text-xs text-white font-medium relative overflow-hidden"
                            initial={{ height: 0 }}
                            animate={{ height: "100%" }}
                            transition={{ duration: 1, delay: 0.4 }}
                        >
                            <motion.div
                                className="absolute top-0 left-0 right-0 h-1 bg-indigo-200"
                                animate={{ opacity: [0, 1, 0], y: [0, 100, 0] }}
                                transition={{ duration: 4, repeat: Infinity, repeatType: "loop" }}
                            />
                            <span className="mb-1 relative z-10">{widget.phases.deep}</span>
                        </motion.div>
                        <motion.div
                            className="h-full w-1/5 bg-indigo-300 flex items-end justify-center text-xs text-indigo-700 font-medium relative overflow-hidden"
                            initial={{ height: 0 }}
                            animate={{ height: "100%" }}
                            transition={{ duration: 1, delay: 0.6 }}
                        >
                            <span className="mb-1 relative z-10">{widget.phases.rem}</span>
                        </motion.div>
                        <motion.div
                            className="h-full w-3/20 bg-indigo-100 flex items-end justify-center text-xs text-indigo-700 font-medium"
                            initial={{ height: 0 }}
                            animate={{ height: "100%" }}
                            transition={{ duration: 1, delay: 0.8 }}
                        >
                            <span className="mb-1 relative z-10">{widget.phases.awake}</span>
                        </motion.div>
                    </div>
                </div>

                {/* Sleep quality */}
                <div className="flex justify-between items-center mb-3 bg-indigo-50/50 p-2 rounded-lg">
                    <span className="text-sm text-gray-600">{widget.quality.label}</span>
                    <div className="flex items-center">
                        <span className="text-sm font-medium text-indigo-700 ml-1">{widget.quality.value}</span>
                        <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <motion.div
                                    key={star}
                                    className="text-yellow-400 text-sm cursor-pointer"
                                    initial={{ opacity: 0, scale: 0 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.1 * star }}
                                    whileHover={{ scale: 1.3, rotate: [0, 15, -15, 0], transition: { duration: 0.5 } }}
                                >
                                    <Star className="w-4 h-4 fill-current" />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sub-widgets */}
            <div className="grid grid-cols-2 gap-3 mt-3">
                {/* Sleep time widget */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-xl p-3 border border-indigo-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-indigo-700 font-medium">{widget.sleepTime.label}</span>
                        <motion.div
                            className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center"
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 4, repeat: Infinity, repeatDelay: 2 }}
                        >
                            <Moon className="w-4 h-4 text-indigo-600" />
                        </motion.div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-500">{widget.sleepTime.range}</span>
                            <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-md">{widget.sleepTime.status}</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-1">
                            <motion.div
                                className="h-full bg-gradient-to-r from-blue-300 to-blue-500 rounded-full"
                                style={{ width: "67%", marginLeft: isRTL ? "auto" : "15%", marginRight: isRTL ? "15%" : "auto" }}
                                initial={{ width: 0 }}
                                animate={{ width: "67%" }}
                                transition={{ duration: 1 }}
                            />
                        </div>
                        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                            {widget.timeLabels.map((label: string, index: number) => (
                                <span key={index}>{label}</span>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Heart rate during sleep */}
                <motion.div
                    className="bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-sm rounded-xl p-3 border border-indigo-50 shadow-sm"
                    whileHover={{ scale: 1.03, boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-indigo-700 font-medium">{widget.heartRate.label}</span>
                        <motion.div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center" animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.8, repeat: Infinity }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="#EC4899" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                            </svg>
                        </motion.div>
                    </div>
                    <div className="flex flex-col">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs text-gray-500">{widget.heartRate.sublabel}</span>
                            <span className="text-xs bg-pink-50 text-pink-600 px-1.5 py-0.5 rounded-md">{widget.heartRate.value}</span>
                        </div>
                        <div className="h-6 relative">
                            <svg width="100%" height="24" viewBox="0 0 100 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <motion.path
                                    d="M0 12H20L25 5L30 19L35 12L40 15L45 7L50 12L55 9L60 17L65 3L70 12L75 8L80 14L85 6L90 12L95 9L100 12"
                                    stroke="#EC4899"
                                    strokeWidth="1.5"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 1.5 }}
                                />
                            </svg>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Tips */}
            <motion.div
                className="mt-3 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg text-xs text-indigo-700 border border-indigo-100 shadow-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
                whileHover={{ scale: 1.02, boxShadow: "0 4px 12px rgba(99, 102, 241, 0.1)" }}
            >
                <div className="flex items-start">
                    <motion.div
                        className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center ml-2 flex-shrink-0"
                        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                        <Bed className="w-4 h-4 text-indigo-700" />
                    </motion.div>
                    <div>
                        <p className="font-medium mb-1">{widget.tips.title}</p>
                        <ul className="list-disc list-inside space-y-1 pr-1">
                            {widget.tips.items.map((tip: string, index: number) => (
                                <li key={index}>{tip}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default SleepWidget;
